import { useState, useEffect, useMemo } from "react";
import { Item, ItemContent, ItemTitle, ItemActions } from "@/ui/item";
import { Input } from "@/ui/input";
import { Button } from "@/ui/button";
import { Badge } from "@/ui/badge";
import { Label } from "@/ui/label";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/ui/tooltip";
import { IconDeviceFloppy, IconCheck, IconGitPullRequest, IconLock, IconExternalLink } from "@tabler/icons-react";
import { useProjectContext } from "../context/ProjectContext";
import { cn } from "@/lib/utils";
import type { ElementWithPendingEdit } from "@/api/common";

// Format field name to user-friendly label (matches AddItemDialog's getFriendlyName)
function formatFieldName(name: string): string {
  if (!name) return "Field";
  const upper = name.toUpperCase();
  // Semantic mappings for common field names
  if (upper === "HREF" || upper === "URL" || upper === "LINK") return "Url";
  if (upper === "TITLE") return "Title";
  if (upper === "DESCRIPTION" || upper === "DESC") return "Description";
  if (upper === "SUBTITLE") return "Subtitle";
  if (upper === "PRICE") return "Price";
  if (upper === "DATE") return "Date";
  if (upper === "AUTHOR") return "Author";
  if (upper === "NAME") return "Name";
  if (upper === "ROLE") return "Role";
  if (upper === "BADGE") return "Badge";
  if (upper === "LABEL") return "Label";
  if (upper === "TEXT" || upper === "CONTENT") return "Text";
  // Fallback: title case
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

// Extract field label from element name (e.g., "SwiftPay Name" -> "Name")
function getFieldLabel(elementName: string): string {
  // Try to extract the last part after common patterns
  const parts = elementName.split(" ");
  if (parts.length > 1) {
    // Return last part formatted (e.g., "NAME" -> "Name")
    return formatFieldName(parts[parts.length - 1]!);
  }
  return formatFieldName(elementName);
}

interface CompoundElementRowProps {
  elements: ElementWithPendingEdit[];
  sectionId: string;
  sectionName: string;
  projectId: string;
}

/**
 * Renders multiple related elements as a single compound item.
 * Used for AI-created elements that belong to the same "card" or "item"
 * (e.g., Name + Category + Description for a portfolio card)
 */
export function CompoundElementRow({
  elements,
  sectionId,
  sectionName,
  projectId: _projectId
}: CompoundElementRowProps) {
  void _projectId; // Reserved for future use (e.g., undo mutations)
  const { saveEdit, hasEdit, getEdit } = useProjectContext();

  // Check if any element has a pending PR
  const hasPendingPR = elements.some(el => !!el.pendingEdit);
  const pendingElement = elements.find(el => !!el.pendingEdit);

  // Check how many elements have local edits
  const editedCount = elements.filter(el => hasEdit(el.id)).length;
  const hasSavedEdits = editedCount > 0;

  // Build field list from elements
  const fields = useMemo(() => {
    return elements.map(el => ({
      element: el,
      key: el.id,
      label: getFieldLabel(el.name || "Field"),
      originalValue: el.currentValue || "",
      existingEdit: getEdit(el.id),
    }));
  }, [elements, getEdit]);

  // State for all field values
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const field of fields) {
      initial[field.key] = field.existingEdit?.newValue ?? field.originalValue;
    }
    return initial;
  });

  // Sync state when elements change
  useEffect(() => {
    const newValues: Record<string, string> = {};
    for (const field of fields) {
      if (!hasEdit(field.element.id)) {
        newValues[field.key] = field.originalValue;
      } else {
        newValues[field.key] = fieldValues[field.key] ?? field.originalValue;
      }
    }
    setFieldValues(newValues);
  }, [elements, fields, hasEdit]);

  // Check if any field has changed
  const isDirty = useMemo(() => {
    for (const field of fields) {
      const currentVal = fieldValues[field.key] || "";
      if (currentVal !== field.originalValue) return true;
    }
    return false;
  }, [fields, fieldValues]);

  const handleFieldChange = (key: string, value: string) => {
    setFieldValues(prev => ({ ...prev, [key]: value }));
  };

  // Save all modified fields
  const handleSaveAll = () => {
    for (const field of fields) {
      const currentVal = fieldValues[field.key] || "";
      const changed = currentVal !== field.originalValue;

      if (changed || hasEdit(field.element.id)) {
        saveEdit({
          elementId: field.element.id,
          elementName: field.element.name || "Untitled",
          sectionId,
          sectionName,
          originalValue: field.originalValue,
          newValue: currentVal,
          sourceFile: field.element.sourceFile || "",
          sourceLine: field.element.sourceLine || 0,
          sourceContext: field.element.sourceContext || "",
          pageUrl: field.element.pageUrl || "/",
        });
      }
    }
  };

  // Compute button state
  const savedValues = fields.reduce((acc, f) => {
    acc[f.key] = f.existingEdit?.newValue ?? f.originalValue;
    return acc;
  }, {} as Record<string, string>);

  const hasUnsavedChanges = fields.some(f => fieldValues[f.key] !== savedValues[f.key]);

  // Use first element's name as the compound item title (strip the field part)
  const firstElement = elements[0];
  const compoundTitle = useMemo(() => {
    const name = firstElement?.name || "Item";
    const parts = name.split(" ");
    if (parts.length > 1) {
      // Remove last part (the field name) to get base title
      return parts.slice(0, -1).join(" ");
    }
    return name;
  }, [firstElement]);

  return (
    <Item
      variant="outline"
      className={cn(
        "flex-col items-stretch transition-colors",
        hasSavedEdits && "border-l-2 border-l-primary bg-primary/5",
        hasPendingPR && "border-l-2 border-l-amber-500 bg-amber-500/5"
      )}
    >
      <div className="flex items-center gap-3 w-full">
        <ItemContent className="flex-1">
          <div className="flex items-center gap-2">
            <ItemTitle>{compoundTitle}</ItemTitle>
            {hasPendingPR && pendingElement?.pendingEdit && (
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="warning" className="text-[10px] gap-1">
                    <IconGitPullRequest className="w-3 h-3" />
                    PR #{pendingElement.pendingEdit.pullRequest.githubPrNumber}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="top">
                  Editing blocked until PR is merged or closed
                </TooltipContent>
              </Tooltip>
            )}
            {!hasPendingPR && hasSavedEdits && (
              <Badge variant="default" className="text-[10px] bg-primary/20 text-primary">
                {editedCount} edit{editedCount !== 1 ? "s" : ""}
              </Badge>
            )}
          </div>
        </ItemContent>

        <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-amber-500">
          {elements.length} fields
        </Badge>

        <ItemActions>
          {hasPendingPR ? (
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger>
                  <IconLock className="w-3.5 h-3.5 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  Editing blocked until PR is merged or closed
                </TooltipContent>
              </Tooltip>
              {pendingElement?.pendingEdit && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="hover:bg-zinc-800 hover:text-zinc-100 text-zinc-400 transition-colors"
                  render={
                    <a
                      href={pendingElement.pendingEdit.pullRequest.githubPrUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none' }}
                    />
                  }
                >
                  View PR
                  <IconExternalLink className="w-3 h-3 ml-1" />
                </Button>
              )}
            </div>
          ) : (
            <Button
              size="sm"
              variant={isDirty || hasUnsavedChanges ? "default" : hasSavedEdits ? "outline" : "ghost"}
              onClick={handleSaveAll}
              disabled={!isDirty && !hasUnsavedChanges}
            >
              {hasSavedEdits && !hasUnsavedChanges ? (
                <IconCheck className="w-3.5 h-3.5" />
              ) : (
                <IconDeviceFloppy className="w-3.5 h-3.5" />
              )}
              {hasSavedEdits && !hasUnsavedChanges ? "Saved" : "Save All"}
            </Button>
          )}
        </ItemActions>
      </div>

      {/* Dynamic Field Inputs */}
      <div className="flex flex-col gap-2 w-full pt-1">
        {fields.map((field) => (
          <div key={field.key} className="flex flex-col gap-1">
            <Label
              htmlFor={`compound-${field.key}`}
              className="text-xs text-muted-foreground"
            >
              {field.label}
            </Label>
            <Input
              id={`compound-${field.key}`}
              value={hasPendingPR ? field.originalValue : (fieldValues[field.key] || "")}
              onChange={(e) => handleFieldChange(field.key, e.target.value)}
              placeholder="Enter text"
              disabled={hasPendingPR}
              className={cn(
                hasEdit(field.element.id) && "border-primary/50",
                hasPendingPR && "opacity-60 cursor-not-allowed"
              )}
            />
          </div>
        ))}
      </div>
    </Item>
  );
}
